import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Icons,
  iconSize,
  Link,
  palette,
  PinMap,
  Stack,
  TagChip,
  Typography,
} from '@munifocus/ui';
import type { TriageQueueItem } from '@munifocus/contracts-gov';
import { formatTriageDate } from '../lib/triage-dates';

/** A file a report can be folded into, and how it came to be offered. */
export interface FoldTarget {
  id: string;
  reference: string;
  /** Metres from the report's pin, or null when it matched on the address. */
  distanceM: number | null;
}

/**
 * The open files a report could be folded into: those near its pin, then any
 * at the same address. Nearest first, each file once.
 */
export function foldTargets(item: TriageQueueItem): FoldTarget[] {
  const out = new Map<string, FoldTarget>();
  for (const m of item.citizenReport?.nearby ?? []) {
    if (m.kind === 'file' && !out.has(m.id)) {
      out.set(m.id, { id: m.id, reference: m.reference, distanceM: m.distanceM });
    }
  }
  for (const f of item.openFiles) {
    if (!out.has(f.id)) out.set(f.id, { id: f.id, reference: f.reference, distanceM: null });
  }
  return [...out.values()];
}

/**
 * What a resident's citizen report adds to a triage item: the category they
 * chose, where they put the pin, their photos, and what else is near.
 *
 * The nearby list is offered, never acted on — the officer decides whether a
 * report is a duplicate. Photo links are short-lived and open in a new tab.
 */
export function CitizenReportDetail({ item }: { item: TriageQueueItem }) {
  const { t, i18n } = useTranslation();
  const report = item.citizenReport;
  if (!report) return null;
  const located = report.latitude !== null && report.longitude !== null;
  const point = located
    ? { latitude: report.latitude as number, longitude: report.longitude as number }
    : null;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, alignItems: 'center' }}>
        {report.category && (
          <TagChip label={t(`common.citizenReports.category.${report.category}`)} />
        )}
        {report.fromPortalAccount && (
          <Typography variant="caption" sx={{ color: palette.text.muted }}>
            {t('gov.triage.fromPortalAccount')}
          </Typography>
        )}
        {report.updatesConsent && (
          <Typography variant="caption" sx={{ color: palette.text.muted }}>
            {t('gov.triage.reporterGetsUpdates')}
          </Typography>
        )}
      </Stack>

      {point && (
        <PinMap
          value={point}
          center={point}
          height={200}
          label={t('gov.triage.mapLabel', { address: item.subjectAddress })}
          attribution={t('gov.triage.mapAttribution')}
          zoomInLabel={t('gov.triage.zoomIn')}
          zoomOutLabel={t('gov.triage.zoomOut')}
          markers={report.nearby.map((m) => ({
            id: `${m.kind}-${m.id}`,
            label: m.reference,
            latitude: m.latitude,
            longitude: m.longitude,
          }))}
        />
      )}
      {point && report.locationSource && (
        <Typography variant="caption" sx={{ color: palette.text.muted }}>
          {t(`gov.triage.locationSource.${report.locationSource}`)}
        </Typography>
      )}

      {report.photos.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {report.photos.map((photo, index) => (
            <Box
              key={photo.id}
              component="a"
              href={photo.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('gov.triage.openPhoto', { n: index + 1 })}
              sx={{ display: 'block', lineHeight: 0 }}
            >
              <Box
                component="img"
                src={photo.url}
                alt={t('gov.triage.photoAlt', { n: index + 1 })}
                sx={{
                  width: 96,
                  height: 96,
                  objectFit: 'cover',
                  borderRadius: 1,
                  border: `1px solid ${palette.border.line}`,
                }}
              />
            </Box>
          ))}
        </Stack>
      )}

      {report.nearby.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {t('gov.triage.nearbyTitle')}
          </Typography>
          <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 0, listStyle: 'none' }}>
            {report.nearby.map((m) => (
              <Stack
                key={`${m.kind}-${m.id}`}
                component="li"
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center' }}
              >
                <Box aria-hidden sx={{ display: 'flex', color: palette.text.muted }}>
                  {m.kind === 'file' ? (
                    <Icons.FileText size={iconSize.sm} />
                  ) : (
                    <Icons.MapPin size={iconSize.sm} />
                  )}
                </Box>
                <Typography variant="body2">
                  {m.kind === 'file' ? (
                    <Link component={RouterLink} to={`/files/${m.id}`}>
                      {m.reference}
                    </Link>
                  ) : (
                    m.reference
                  )}{' '}
                  ·{' '}
                  {t(m.kind === 'file' ? 'gov.triage.nearbyFile' : 'gov.triage.nearbyReport', {
                    distance: m.distanceM,
                    date: formatTriageDate(m.createdAt, i18n.language),
                    status:
                      m.kind === 'file'
                        ? t(`gov.triage.nearbyStage.${m.status}`, { defaultValue: m.status })
                        : t(`gov.triage.status.${m.status}`),
                  })}
                  {m.sameCategory && ` · ${t('gov.triage.sameCategory')}`}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
